<script setup lang="ts">
import { computed, toRef } from 'vue';
import { staticName } from '@/lib/asset';
import { i18n, t } from '@/i18n';

const locale = toRef(i18n.options, 'locale');

const avatarPaths = [
  `${staticName}/static-assets/avatar/bird.webp`,
  `${staticName}/static-assets/avatar/cat.webp`,
  `${staticName}/static-assets/avatar/dog.webp`,
  `${staticName}/static-assets/avatar/chihuahua.webp`,
  `${staticName}/static-assets/avatar/shih-tzu.webp`,
  `${staticName}/static-assets/avatar/toy-poodle.webp`,
];

const testimonials = computed(() => {
  void locale.value;

  return (['1', '2', '3', '4', '5', '6'] as const).map((id, index) => ({
    id: index + 1,
    name: t(`landing_home.testimonials.${id}.name`),
    role: t(`landing_home.testimonials.${id}.role`),
    content: t(`landing_home.testimonials.${id}.content`),
    avatar: avatarPaths[index]!,
  }));
});

const headingLine1 = computed(() => {
  void locale.value;
  return t('landing_home.testimonials.heading_line1');
});

const headingLine2 = computed(() => {
  void locale.value;
  return t('landing_home.testimonials.heading_line2');
});

const subheadingLines = computed(() => {
  void locale.value;
  return t('landing_home.testimonials.subheading').split('\n');
});

const subheadingClass = computed(() =>
  locale.value === 'ja'
    ? 'text-black/80 text-center text-[14px] leading-[1.6] mb-16 px-4 md:px-8 max-w-[920px] mx-auto'
    : 'text-black/80 text-center text-base md:text-2xl mb-16 px-4 md:px-64'
);
</script>

<template>
  <section class="bg-white py-16 md:py-32 px-4">
    <div class="max-w-[1320px] mx-auto">
      <h2
        class="font-boldonse text-[clamp(1.5rem,3vw,2rem)] text-black/90 uppercase leading-[1.6] mb-4 text-center landing-title-multiline landing-title-section-md"
      >
        <span class="block landing-title-line">{{ headingLine1 }}</span>
        <span class="block landing-title-line">{{ headingLine2 }}</span>
      </h2>
      <p :class="subheadingClass">
        <template v-for="(line, lineIndex) in subheadingLines" :key="lineIndex">
          <br v-if="lineIndex > 0" />{{ line }}
        </template>
      </p>

      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
        <div
          v-for="testimonial in testimonials"
          :key="testimonial.id"
          class="bg-black/[0.03] rounded-2xl p-4 md:p-4 flex flex-col"
        >
          <p class="text-black/80 text-[1rem] leading-[1.3]">{{ testimonial.content }}</p>
          <div class="flex items-center gap-2 mt-auto pt-24">
            <img
              :src="testimonial.avatar"
              :alt="testimonial.name"
              class="w-10 h-10 rounded-full border border-black/[0.02] bg-black/10 object-cover"
            />
            <div>
              <p class="font-normal text-sm text-black/90">{{ testimonial.name }}</p>
              <p class="text-sm text-black/50">
                {{ testimonial.role }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
